/* GLAMRDiP — Junip ↔ static-stars bridge
 *
 * Reads rating + review count from Junip's product-summary widget after
 * Junip's external script populates it, then rewrites the visible green
 * star block + count to reflect the live numbers.
 *
 * Markup contract per consuming section (colour-pdp, accessory-hero,
 * liquid-hero):
 *
 *   <div class="..._rating-row" data-junip-bound data-product-id="123">
 *     <span class="..._rating--junip-bound">
 *       {% render 'gd-star-rating', rating: rating %}
 *       <span data-junip-count [hidden]>(N)</span>
 *     </span>
 *     <span class="junip-product-summary gd-junip-source" data-product-id="123"></span>
 *   </div>
 *
 * The .gd-junip-source span is positioned off-screen via CSS but stays
 * in the DOM so Junip mounts content into it. We observe DOM mutations
 * on it to detect Junip's render, then read the rating/count and update
 * the visible static stars.
 *
 * Rating extraction strategy (in order):
 *   1. data-rating / data-reviews-count attributes on the source span
 *      (Junip v1 sometimes sets these)
 *   2. data-rating / data-rating-count on a Junip-injected child
 *   3. Parse aria-label like "4.8 out of 5 stars" or text "4.8 (123)"
 *   4. Bail — leave fallback rating unchanged
 *
 * Star DOM update mirrors gd-star-rating.liquid logic:
 *   - 5 spans, classes gd-star--full / --half / --empty
 *   - .gd-star--half hosts an absolute .gd-star__empty-half overlay
 *
 * Idempotent: bound rows get [data-gd-junip-bridged] flag so multiple
 * mutation events don't re-render redundantly.
 */
(function () {
  'use strict';

  function parseRatingFrom(source) {
    if (!source) return null;

    // 1) data-* on the source span itself
    var rAttr = source.getAttribute('data-rating');
    var cAttr = source.getAttribute('data-reviews-count') ||
                source.getAttribute('data-review-count') ||
                source.getAttribute('data-count');
    if (rAttr && parseFloat(rAttr) > 0) {
      return { rating: parseFloat(rAttr), count: parseInt(cAttr || '0', 10) || 0 };
    }

    // 2) data-* on a Junip-injected child (junip-stars, junip-rating, etc.)
    var child = source.querySelector('[data-rating], [data-junip-rating]');
    if (child) {
      var cr = child.getAttribute('data-rating') || child.getAttribute('data-junip-rating');
      var cc = child.getAttribute('data-reviews-count') ||
               child.getAttribute('data-rating-count') ||
               child.getAttribute('data-count');
      if (cr && parseFloat(cr) > 0) {
        return { rating: parseFloat(cr), count: parseInt(cc || '0', 10) || 0 };
      }
    }

    // 3) Parse aria-label or text content
    //    Patterns: "4.8 out of 5 stars" / "Rated 4.8 out of 5 from 123 reviews"
    var label = (source.getAttribute('aria-label') || source.textContent || '').trim();
    var ratingMatch = label.match(/(\d+(?:\.\d+)?)\s*(?:out of\s*5|\/\s*5|stars?)/i) ||
                      label.match(/^\s*(\d+(?:\.\d+)?)\b/);
    var countMatch = label.match(/(?:from|\()\s*(\d{1,7})\s*(?:reviews?|\)|$)/i) ||
                     label.match(/\((\d{1,7})\)/);
    if (ratingMatch) {
      var r = parseFloat(ratingMatch[1]);
      if (r > 0 && r <= 5) {
        return { rating: r, count: countMatch ? parseInt(countMatch[1], 10) : 0 };
      }
    }

    return null;
  }

  function updateStars(container, rating) {
    var stars = container.querySelectorAll('.gd-star');
    if (stars.length !== 5) return;

    for (var i = 0; i < 5; i++) {
      var star = stars[i];
      var delta = rating - (i + 1);
      var state;
      if (delta >= 0) state = 'full';
      else if (delta >= -0.5) state = 'half';
      else state = 'empty';

      star.classList.remove('gd-star--full', 'gd-star--half', 'gd-star--empty');
      star.classList.add('gd-star--' + state);

      // Manage the half-star empty overlay (matches gd-star-rating.liquid).
      var existingHalf = star.querySelector('.gd-star__empty-half');
      if (state === 'half' && !existingHalf) {
        var overlay = document.createElement('span');
        overlay.className = 'gd-star__empty-half';
        // Mirror snippet's border-radius rule: 0 r r 0
        var radius = (star.style.borderRadius || '0px').split(' ').pop();
        overlay.style.borderRadius = '0 ' + radius + ' ' + radius + ' 0';
        star.insertBefore(overlay, star.firstChild);
      } else if (state !== 'half' && existingHalf) {
        existingHalf.remove();
      }
    }

    // Update aria-label on the wrapper for screen readers.
    var wrapper = container.querySelector('.gd-star-rating');
    if (wrapper) wrapper.setAttribute('aria-label', rating.toFixed(1) + ' out of 5 stars');
  }

  function updateCount(row, count) {
    var countEl = row.querySelector('[data-junip-count]');
    if (!countEl) return;
    if (count > 0) {
      countEl.textContent = '(' + count + ')';
      countEl.hidden = false;
    } else {
      countEl.hidden = true;
    }
  }

  function tryBind(row) {
    if (!row || row.dataset.gdJunipBridged === '1') return;
    var source = row.querySelector('.gd-junip-source');
    if (!source) return;
    var data = parseRatingFrom(source);
    if (!data) return;

    updateStars(row, data.rating);
    updateCount(row, data.count);
    row.dataset.gdJunipBridged = '1';
  }

  function init() {
    var rows = document.querySelectorAll('[data-junip-bound]');
    if (!rows.length) return;

    // Try once on init in case Junip already mounted.
    rows.forEach(tryBind);

    // Observe each Junip source span for content / attribute changes.
    rows.forEach(function (row) {
      var source = row.querySelector('.gd-junip-source');
      if (!source) return;
      if (row.dataset.gdJunipBridged === '1') return;

      var observer = new MutationObserver(function () {
        tryBind(row);
        if (row.dataset.gdJunipBridged === '1') observer.disconnect();
      });
      observer.observe(source, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true
      });

      // Safety timeout — stop watching after 60s if Junip never lands.
      // Joel 2026-05-04: bumped from 10s to 60s to handle the deferred
      // Junip script load. With deferred load, Junip JS doesn't fire
      // until user scrolls toward the review widget — could be 30-50s
      // after page load on slow scrollers. 10s timeout would disconnect
      // the observer before Junip populates the placeholder. 60s is
      // generous enough for nearly all real user behaviour. The static
      // fallback (rating from metafields or 4.8 default) remains
      // visible regardless — never blank.
      setTimeout(function () { observer.disconnect(); }, 60000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
